import { useEffect } from 'react';

type MetaTag = {
  name?: string;
  property?: string;
  content: string;
};

type SeoProps = {
  title: string;
  description: string;
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogUrl: string;
  ogImage: string;
  schema: object;
  noIndex?: boolean;
};

const MANAGED_SELECTOR = 'meta[data-seo="managed"], link[data-seo="managed"], script[data-seo="managed"]';

function upsertMeta({ name, property, content }: MetaTag) {
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let tag = document.head.querySelector<HTMLMetaElement>(selector);

  if (!tag) {
    tag = document.createElement('meta');
    if (name) tag.setAttribute('name', name);
    if (property) tag.setAttribute('property', property);
    tag.setAttribute('data-seo', 'managed');
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
}

export default function Seo({
  title,
  description,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogUrl,
  ogImage,
  schema,
  noIndex = false,
}: SeoProps) {
  useEffect(() => {
    document.title = title;

    document.head.querySelectorAll(MANAGED_SELECTOR).forEach((element) => element.remove());

    upsertMeta({ name: 'description', content: description });
    if (noIndex) {
      upsertMeta({ name: 'robots', content: 'noindex, nofollow' });
    }
    upsertMeta({ property: 'og:type', content: 'website' });
    upsertMeta({ property: 'og:title', content: ogTitle });
    upsertMeta({ property: 'og:description', content: ogDescription });
    upsertMeta({ property: 'og:url', content: ogUrl });
    upsertMeta({ property: 'og:image', content: ogImage });

    const canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', canonicalUrl);
    canonical.setAttribute('data-seo', 'managed');
    document.head.appendChild(canonical);

    const schemaScript = document.createElement('script');
    schemaScript.setAttribute('type', 'application/ld+json');
    schemaScript.setAttribute('data-seo', 'managed');
    schemaScript.textContent = JSON.stringify(schema);
    document.head.appendChild(schemaScript);
  }, [canonicalUrl, description, noIndex, ogDescription, ogImage, ogTitle, ogUrl, schema, title]);

  return null;
}
